import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { RUN_STAGE_LABELS } from '@sfpbxtestbot/contracts';
import { RunTimeline } from '../components/RunTimeline.js';
import { ScreenshotGallery } from '../components/Evidence.js';
import { LogViewer } from '../components/LogViewer.js';
import {
  Button,
  Callout,
  Card,
  EmptyState,
  PageHeader,
  Stat,
  StatusBadge,
  formatDuration,
  formatTimestamp,
} from '../components/primitives.js';
import { api } from '../lib/api.js';
import { useRunStream } from '../lib/useRunStream.js';

type Panel = 'timeline' | 'screenshots' | 'call' | 'logs';

/** One run, in full: the timeline, its evidence, and a printable report. */
export function RunDetailPage(): ReactNode {
  const { runId } = useParams<{ runId: string }>();
  const stream = useRunStream(runId);
  const [panel, setPanel] = useState<Panel>('timeline');
  const [stopping, setStopping] = useState(false);

  const run = stream.run;
  const inProgress = run?.outcome === 'running' || run?.outcome === 'pending';
  const evidence = (run?.steps ?? []).flatMap((step) => step.evidence);

  if (!runId) return null;

  return (
    <div className="stack">
      <PageHeader
        title={run?.label ?? 'Run report'}
        lede={
          run
            ? `${run.stages.map((stage) => RUN_STAGE_LABELS[stage]).join(', ')} · ${run.environment} · tenant ${run.tenantCode}`
            : 'Loading the run.'
        }
        actions={
          <>
            {run ? <StatusBadge outcome={run.outcome} /> : null}
            {inProgress ? (
              <Button
                variant="danger"
                size="small"
                busy={stopping}
                onClick={() => {
                  setStopping(true);
                  void api.stopRun(runId).finally(() => setStopping(false));
                }}
              >
                Stop
              </Button>
            ) : null}
            <a
              className="button button--small"
              href={api.reportUrl(runId)}
              target="_blank"
              rel="noreferrer"
            >
              Open report
            </a>
            <a className="button button--ghost button--small" href={api.reportUrl(runId, true)}>
              Download report
            </a>
            <Link className="button button--ghost button--small" to="/runs">
              All runs
            </Link>
          </>
        }
      />

      {stream.error ? <Callout tone="danger">{stream.error}</Callout> : null}
      {run?.reason ? (
        <Callout tone={run.outcome === 'failed' ? 'danger' : 'warning'}>{run.reason}</Callout>
      ) : null}

      {run ? (
        <Card>
          <div className="row" style={{ gap: 40 }}>
            <Stat value={run.totals?.passed ?? 0} label="Passed" tone="passed" />
            <Stat
              value={run.totals?.failed ?? 0}
              label="Failed"
              tone={(run.totals?.failed ?? 0) > 0 ? 'failed' : undefined}
            />
            <Stat
              value={run.totals?.warning ?? 0}
              label="Needs a look"
              tone={(run.totals?.warning ?? 0) > 0 ? 'warning' : undefined}
            />
            <Stat value={run.totals?.skipped ?? 0} label="Skipped" />
            <Stat value={formatDuration(run.durationMs)} label="Duration" />
          </div>
          <p className="muted" style={{ fontSize: 13, marginTop: 16 }}>
            Started {formatTimestamp(run.startedAt ?? run.createdAt)}
            {run.endedAt ? `, finished ${formatTimestamp(run.endedAt)}` : ''}
            {run.dryRun ? ' · dry run, nothing was changed' : ''}
          </p>
        </Card>
      ) : null}

      <div>
        <div className="tabs" role="tablist">
          {(
            [
              ['timeline', 'Steps'],
              ['screenshots', `Screenshots (${evidence.filter((item) => item.kind === 'screenshot').length})`],
              ['call', `Call (${stream.transcript.length})`],
              ['logs', 'Logs'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              className="tabs__tab"
              aria-selected={panel === id}
              onClick={() => setPanel(id)}
            >
              {label}
            </button>
          ))}
        </div>

        <Card>
          {panel === 'timeline' ? <RunTimeline runId={runId} steps={run?.steps ?? []} /> : null}
          {panel === 'screenshots' ? <ScreenshotGallery runId={runId} evidence={evidence} /> : null}
          {panel === 'logs' ? <LogViewer logs={stream.logs} /> : null}
          {panel === 'call' ? (
            stream.transcript.length === 0 && stream.triggers.length === 0 ? (
              <EmptyState
                title="No call on this run"
                body="This run did not include a live call, so there is no transcript to show."
              />
            ) : (
              <div className="stack">
                {stream.triggers.length > 0 ? (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Phrase spoken</th>
                        <th>Type</th>
                        <th>Reacted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stream.triggers.map((trigger) => (
                        <tr key={trigger.triggerId}>
                          <td>{trigger.phrase}</td>
                          <td className="muted">{trigger.kind}</td>
                          <td>
                            <StatusBadge outcome={trigger.observed ? 'passed' : 'failed'} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : null}

                <div className="transcript">
                  {stream.transcript.map((segment, index) => (
                    <div key={`${segment.at}-${index}`} className={`bubble bubble--${segment.speaker}`}>
                      {segment.speaker !== 'system' ? (
                        <div className="bubble__speaker">{segment.speaker}</div>
                      ) : null}
                      {segment.text}
                    </div>
                  ))}
                </div>
              </div>
            )
          ) : null}
        </Card>
      </div>
    </div>
  );
}
